import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Video } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/home");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    }
    setLoading(false);
  };

  return (
    <div data-testid="login-page" className="page-gradient" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 420, width: '100%', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/landing" data-testid="login-logo-link" style={{ display: 'inline-flex', textDecoration: 'none', justifyContent: 'center' }}>
            <YSVLogo size="hero" />
          </Link>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 16 }}>Sign in to your account</p>
        </div>

        <div className="glass-card" style={{ padding: 32 }}>
          <form data-testid="login-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && <div data-testid="login-error" style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f44336', fontSize: 13 }}>{error}</div>}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Email</label>
              <input className="input-field" data-testid="login-email" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input className="input-field" data-testid="login-password" type={showPw ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" data-testid="login-submit" className="btn-primary" disabled={loading} style={{ width: '100%', marginTop: 8, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--cyan)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              Forgot password?
            </Link>
          </div>

          <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--dark-border)' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              Don't have an account? <Link to="/get-started" style={{ color: 'var(--cyan)', textDecoration: 'none', fontWeight: 600 }}>Sign up</Link>
            </p>
          </div>

          {/* GDO Portal Link */}
          <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--dark-border)' }}>
            <Link to="/gdo" data-testid="gdo-portal-link" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              textDecoration: 'none', color: '#ff6b00', fontSize: 14, fontWeight: 600,
              transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <Video size={18} /> GDO Portal Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
